import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { DOMAINES, NIVEAUX, EXERCICES } from '../data/coursTerrain';
import { toast } from 'sonner';

const LS_KEY = 'cdr-terrain-grille';

const loadGrille = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '{}');
  } catch { return {}; }
};
const saveGrille = (data) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch { /* noop */ }
};

export default function CoursTerrain() {
  const navigate = useNavigate();
  const [eleveNom, setEleveNom] = useState('');
  const [dateSeance, setDateSeance] = useState(() => new Date().toISOString().slice(0, 10));
  const [exerciceId, setExerciceId] = useState(EXERCICES[0].id);
  const [evaluation, setEvaluation] = useState({}); // { critereId: niveau }
  const [notes, setNotes] = useState('');
  const [savedList, setSavedList] = useState(loadGrille());

  const exercice = EXERCICES.find(e => e.id === exerciceId);

  const score = useMemo(() => {
    const total = DOMAINES.reduce((acc, d) => acc + d.criteres.length, 0);
    let points = 0;
    let observed = 0;
    Object.values(evaluation).forEach(v => {
      if (v === 'acquis') { points += 3; observed++; }
      else if (v === 'encours') { points += 2; observed++; }
      else if (v === 'acquerir') { points += 1; observed++; }
    });
    const max = observed * 3;
    const pct = max > 0 ? Math.round((points / max) * 100) : 0;
    return { points, max, pct, observed, total };
  }, [evaluation]);

  const setCritere = (id, niveau) => {
    setEvaluation(prev => ({ ...prev, [id]: prev[id] === niveau ? 'na' : niveau }));
  };

  const handleSave = () => {
    if (!eleveNom.trim()) {
      toast.error('Indique le prénom de l\'élève.');
      return;
    }
    const key = `${dateSeance}_${eleveNom.trim()}_${exerciceId}`;
    const data = { ...savedList, [key]: { eleveNom, dateSeance, exerciceId, evaluation, notes, score: score.pct, savedAt: new Date().toISOString() } };
    saveGrille(data);
    setSavedList(data);
    toast.success(`Grille enregistrée pour ${eleveNom}`);
  };

  const handleReset = () => {
    setEvaluation({});
    setNotes('');
    toast.info('Grille réinitialisée');
  };

  const handleLoad = (key) => {
    const saved = savedList[key];
    if (!saved) return;
    setEleveNom(saved.eleveNom);
    setDateSeance(saved.dateSeance);
    setExerciceId(saved.exerciceId);
    setEvaluation(saved.evaluation || {});
    setNotes(saved.notes || '');
    toast.info(`Grille chargée : ${saved.eleveNom}`);
  };

  const handleDelete = (key) => {
    const copy = { ...savedList };
    delete copy[key];
    saveGrille(copy);
    setSavedList(copy);
    toast.info('Grille supprimée');
  };

  const grilles = Object.entries(savedList).sort((a, b) => (b[1].savedAt || '').localeCompare(a[1].savedAt || ''));

  return (
    <div className="space-y-5" data-testid="cours-terrain-page">
      {/* Hero */}
      <section className="mc-card relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-60 h-60 rounded-full blur-3xl opacity-30" style={{ background: '#cf2031' }} />
        <div className="relative p-6 sm:p-8">
          <Button onClick={() => navigate('/enseignant')} variant="outline" size="sm" className="rounded-full bg-white/10 border-white/20 text-foreground hover:bg-white/20 hover:text-foreground mb-4" data-testid="back-to-dashboard">
            <Icons.ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard enseignant
          </Button>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-[10px] font-black text-foreground uppercase tracking-widest mb-3">
            <Icons.Bike className="w-3 h-3" /> Cours pratique / Terrain
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-foreground leading-tight">
            Grille d'évaluation live.<br />
            <span className="text-[#ff7a84]">Plateau · Circulation.</span>
          </h1>
          <p className="text-sm sm:text-base text-foreground/80 mt-3 max-w-xl">
            Coche en temps réel les critères observés pendant la séance. Enregistre, reprends, imprime : ta traçabilité pédagogique, simplement.
          </p>
        </div>
      </section>

      {/* Infos séance */}
      <section className="mc-card p-5" data-testid="seance-info">
        <h2 className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5"><Icons.User className="w-3.5 h-3.5" /><span>Infos séance</span></h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-bold text-muted-foreground">Élève</label>
            <input
              type="text"
              value={eleveNom}
              onChange={(e) => setEleveNom(e.target.value)}
              placeholder="Prénom Nom"
              data-testid="input-eleve-nom"
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground">Date</label>
            <input
              type="date"
              value={dateSeance}
              onChange={(e) => setDateSeance(e.target.value)}
              data-testid="input-date-seance"
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground">Exercice</label>
            <select
              value={exerciceId}
              onChange={(e) => setExerciceId(e.target.value)}
              data-testid="select-exercice"
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
            >
              {EXERCICES.map(ex => (
                <option key={ex.id} value={ex.id}>{ex.categorie} · {ex.nom} ({ex.duree})</option>
              ))}
            </select>
          </div>
        </div>

        {exercice && (
          <div className="mt-4 p-4 rounded-xl bg-[hsl(var(--primary-soft))] border border-[hsl(var(--primary))]/20">
            <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1 flex items-center gap-1.5"><Icons.Target className="w-3 h-3" /><span>Objectif</span></div>
            <p className="text-sm font-semibold">{exercice.objectif}</p>
            <div className="flex gap-2 flex-wrap mt-2">
              {exercice.focus.map(k => {
                const d = DOMAINES.find(dd => dd.key === k);
                if (!d) return null;
                return (
                  <span key={k} className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-1" style={{ background: `${d.color}20`, color: d.color }}>
                    {d.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Résumé score */}
      <section className="mc-card p-5 flex items-center justify-between flex-wrap gap-4" data-testid="score-resume">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">📊 Score global</div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-4xl font-black tabular-nums" style={{ color: score.pct >= 70 ? '#18794e' : score.pct >= 40 ? '#f59e0b' : '#b42318' }}>
              {score.pct}%
            </span>
            <span className="text-sm text-muted-foreground font-semibold">
              {score.observed}/{score.total} critères observés
            </span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={handleReset} variant="outline" size="sm" data-testid="reset-grille">
            <Icons.RotateCcw className="w-4 h-4 mr-1.5" /> Réinitialiser
          </Button>
          <Button onClick={handleSave} className="mc-btn-primary" size="sm" data-testid="save-grille">
            <Icons.Save className="w-4 h-4 mr-1.5" /> Enregistrer
          </Button>
        </div>
      </section>

      {/* Grille d'évaluation */}
      <section className="space-y-4" data-testid="grille-evaluation">
        {DOMAINES.map(domaine => {
          const Icon = Icons[domaine.icon] || Icons.Check;
          return (
            <div key={domaine.key} className="mc-card p-5" style={{ borderLeft: `6px solid ${domaine.color}` }} data-testid={`domaine-${domaine.key}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-foreground" style={{ background: domaine.color }}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: domaine.color }}>Domaine</div>
                  <h3 className="font-black text-lg leading-tight">{domaine.label}</h3>
                </div>
              </div>

              <div className="space-y-2">
                {domaine.criteres.map(c => {
                  const current = evaluation[c.id] || 'na';
                  return (
                    <div key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/40 transition-colors">
                      <div className="flex-1 text-sm font-semibold">{c.label}</div>
                      <div className="flex gap-1 flex-shrink-0">
                        {NIVEAUX.map(n => (
                          <button
                            key={n.value}
                            onClick={() => setCritere(c.id, n.value)}
                            data-testid={`critere-${c.id}-${n.value}`}
                            title={n.label}
                            className={cn(
                              'w-9 h-9 rounded-lg font-black text-sm transition-all border-2',
                              current === n.value ? 'text-foreground shadow-md scale-105' : 'bg-card text-foreground/50 border-border hover:border-foreground/30'
                            )}
                            style={current === n.value ? { background: n.color, borderColor: n.color } : {}}
                          >
                            {n.short}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>

      {/* Légende */}
      <section className="mc-card p-4" data-testid="legende">
        <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">🗝️ Légende</div>
        <div className="flex flex-wrap gap-2">
          {NIVEAUX.map(n => (
            <div key={n.value} className="flex items-center gap-2 text-xs font-bold rounded-full border border-border px-3 py-1">
              <span className="w-5 h-5 rounded text-foreground flex items-center justify-center text-xs font-black" style={{ background: n.color }}>{n.short}</span>
              {n.label}
            </div>
          ))}
        </div>
      </section>

      {/* Notes */}
      <section className="mc-card p-5" data-testid="notes-seance">
        <label className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><Icons.FileText className="w-3.5 h-3.5" /><span>Notes / Débrief</span></label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder="Points forts, axes de travail, objectifs pour la prochaine séance…"
          data-testid="notes-textarea"
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
        />
      </section>

      {/* Grilles sauvegardées */}
      {grilles.length > 0 && (
        <section className="mc-card p-5" data-testid="grilles-saved">
          <h2 className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-3">📁 Grilles enregistrées ({grilles.length})</h2>
          <div className="space-y-2">
            {grilles.slice(0, 10).map(([key, data]) => (
              <div key={key} className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border hover:bg-muted/40 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{data.eleveNom}</div>
                  <div className="text-xs text-muted-foreground">
                    {data.dateSeance} · {EXERCICES.find(e => e.id === data.exerciceId)?.nom || data.exerciceId} · {data.score}%
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button onClick={() => handleLoad(key)} variant="outline" size="sm" data-testid={`load-${key}`}>
                    <Icons.Upload className="w-3.5 h-3.5" />
                  </Button>
                  <Button onClick={() => handleDelete(key)} variant="outline" size="sm" data-testid={`delete-${key}`}>
                    <Icons.Trash2 className="w-3.5 h-3.5 text-[#b42318]" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
